'use client'

import {
  AuthErrorMessage,
  AuthSubmitButton,
  AuthTextField,
} from '@/features/auth/AuthFormControls'
import {
  AuthSplitLayout,
  AuthStatusLayout,
} from '@/features/auth/AuthPageLayout'
import { Form, Formik } from 'formik'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { object, string } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { CreatePasswordResetLink } from '@/lib/apis/authentication'
import { translateError } from '@/lib/apis/error'
import useApiClient from '@/lib/apis/clients/useApiClient'

const validator = object({
  email: string()
    .default('')
    .required('Email address is required')
    .email('Wrong email address'),
})

const RESEND_COOLDOWN_SECONDS = 60

export default function ForgotPasswordForm() {
  const [message, setMessage] = useState('')
  const [sentEmail, setSentEmail] = useState('')
  const [secondsUntilResend, setSecondsUntilResend] = useState(0)
  const api = useApiClient()
  const mutation = useMutation(CreatePasswordResetLink.mutation(api))

  useEffect(() => {
    if (secondsUntilResend === 0) return

    const countdownTimer = window.setTimeout(() => {
      setSecondsUntilResend((seconds) => Math.max(0, seconds - 1))
    }, 1000)

    return () => window.clearTimeout(countdownTimer)
  }, [secondsUntilResend])

  function resendResetLink() {
    if (!sentEmail || secondsUntilResend > 0) return

    mutation.mutate(
      { username: sentEmail },
      {
        onSuccess: () => {
          setMessage('')
          setSecondsUntilResend(RESEND_COOLDOWN_SECONDS)
        },
        onError: (error) => setMessage(translateError(error)),
      },
    )
  }

  return (
    <Formik
      initialValues={validator.cast({})}
      onSubmit={(values, { setSubmitting }) => {
        mutation.mutate(
          {
            username: values.email,
          },
          {
            onSuccess: () => {
              setSentEmail(values.email)
              setMessage('')
              setSecondsUntilResend(RESEND_COOLDOWN_SECONDS)
            },
            onError: (error) => {
              setMessage(translateError(error))
            },
            onSettled: () => {
              setSubmitting(false)
            },
          },
        )
      }}
      validationSchema={validator}
    >
      {(props) =>
        sentEmail ? (
          <AuthStatusLayout
            title="Check your email"
            body={
              <>
                We&apos;ve sent sign-in instructions to {sentEmail}. They
                include every available way to access your account.
                {message && (
                  <span className="mt-2 block font-medium text-[#a2382e]">
                    {message}
                  </span>
                )}
              </>
            }
            action={
              <p className="flex items-center gap-1.5 text-[13.5px] leading-[18px] text-[#82828b] lg:gap-[5px]">
                Didn&apos;t get it?
                <button
                  type="button"
                  onClick={resendResetLink}
                  disabled={mutation.isPending || secondsUntilResend > 0}
                  className="font-semibold text-[#2563eb] hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {mutation.isPending
                    ? 'Sending…'
                    : secondsUntilResend > 0
                      ? `Resend in ${secondsUntilResend}s`
                      : 'Resend'}
                </button>
              </p>
            }
          />
        ) : (
          <AuthSplitLayout
            illustrationTitle="Let's get you back in."
            illustrationBody="Your consults, scripts and results are where you left them."
          >
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-bold leading-[30px] text-[#0b0b0f] lg:text-[26px] lg:leading-[33px] lg:tracking-[-0.01em]">
                Get back into your account
              </h1>
              <p className="text-[13.5px] leading-[19px] text-[#82828b] lg:text-[15px] lg:leading-[21px]">
                Enter your email and we&apos;ll send your available sign-in
                options.
              </p>
            </div>

            <Form className="mt-6 flex flex-col lg:mt-5">
              <AuthTextField
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="you@email.com"
                hasError={!!message}
              />
              {message && (
                <AuthErrorMessage className="mt-[18px] lg:mt-5">
                  {message}
                </AuthErrorMessage>
              )}
              <AuthSubmitButton
                loading={props.isSubmitting}
                className={message ? 'mt-[18px] lg:mt-6' : 'mt-6'}
              >
                Send sign-in options
              </AuthSubmitButton>
            </Form>

            <div className="mt-5 flex justify-center">
              <Link
                href="/login"
                className="text-[13.5px] font-semibold leading-[18px] text-[#2563eb] hover:underline"
              >
                ← Back to sign in
              </Link>
            </div>
          </AuthSplitLayout>
        )
      }
    </Formik>
  )
}
